import { useCallback, useState } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useNotificationsContext } from "../contexts/NotificationsContext";
import type { NotificationResponse } from "../utils/models";

// Notification types emitted by the BE when a manager approves a nurse's pickup request.
// Sourced from `NotificationType.Approved` / `NotificationType.ApprovedReplace` in `backend/model.py`.
const APPROVAL_NOTIFICATION_TYPES: ReadonlySet<string> = new Set(["approved", "approved_replace"]);

type ApprovalCelebrationResult = {
	approvals: NotificationResponse[];
	isOpen: boolean;
	// True while notifications are still loading (so `isOpen` isn't trusted yet).
	isLoading: boolean;
	dismiss: () => Promise<void>;
};

export function useApprovalCelebration(): ApprovalCelebrationResult {
	const { allNotifications, markNotificationsSeen, isLoading } = useNotificationsContext();
	const { track, trackError } = useAnalytics();

	const [dismissedIds, setDismissedIds] = useState<ReadonlySet<number>>(new Set());

	const approvals = allNotifications.filter((n) => APPROVAL_NOTIFICATION_TYPES.has(n.notification_type) && !n.isSeen && !dismissedIds.has(n.id));

	const dismiss = useCallback(async (): Promise<void> => {
		if (approvals.length === 0) return;

		const ids = approvals.map((n) => n.id);
		setDismissedIds((prev) => new Set([...prev, ...ids]));
		track("approval_celebration_modal_acknowledge_start", { request_count: ids.length });

		try {
			await markNotificationsSeen(ids);
			track("approval_celebration_modal_acknowledge_success", { request_count: ids.length });
		} catch (err) {
			trackError("approval_celebration_modal_acknowledge_error", {
				failed_count: ids.length,
				total_count: ids.length,
				error_message: err instanceof Error ? err.message : String(err),
			});
		}
	}, [approvals, markNotificationsSeen, track, trackError]);

	return {
		approvals,
		isOpen: approvals.length > 0,
		isLoading,
		dismiss,
	};
}
